import { Close as CloseIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOrderStatusValidation } from "../../../hooks/useOrderStatus";
import {
  CreateOrderStatusRequest,
  OrderStatus,
  OrderStatusFormErrors,
  OrderStatusModalMode,
} from "../../../types/admin";

interface OrderStatusFormModalProps {
  open: boolean;
  mode: OrderStatusModalMode;
  status?: OrderStatus;
  allStatuses?: OrderStatus[];
  onClose: () => void;
  onSuccess: (status: OrderStatus) => void;
}

const OrderStatusFormModal: React.FC<OrderStatusFormModalProps> = ({
  open,
  mode,
  status,
  allStatuses = [],
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateOrderStatusRequest>({
    name: "",
    displayName: "",
    description: "",
    color: "#2196f3",
    icon: "",
    sortOrder: 0,
    isActive: true,
    canTransitionTo: [],
    requiresTrackingNumber: false,
    requiresNote: false,
  });

  const [errors, setErrors] = useState<OrderStatusFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { validateStatus, validateStatusUpdate } = useOrderStatusValidation();

  // Predefined color options
  const colorOptions = [
    "#2196f3", // Blue
    "#4caf50", // Green
    "#ff9800", // Orange
    "#f44336", // Red
    "#9c27b0", // Purple
    "#00bcd4", // Cyan
    "#ffeb3b", // Yellow
    "#795548", // Brown
    "#607d8b", // Blue Grey
    "#e91e63", // Pink
  ];

  // Predefined icon options
  const iconOptions = [
    "pending",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
    "hold",
    "returned",
    "refunded",
    "completed",
    "in_progress",
  ];

  useEffect(() => {
    if (mode === "edit" && status) {
      setFormData({
        name: status.name,
        displayName: status.displayName,
        description: status.description || "",
        color: status.color,
        icon: status.icon || "",
        sortOrder: status.sortOrder,
        isActive: status.isActive,
        canTransitionTo: status.canTransitionTo,
        requiresTrackingNumber: status.requiresTrackingNumber,
        requiresNote: status.requiresNote,
      });
    } else if (mode === "create") {
      setFormData({
        name: "",
        displayName: "",
        description: "",
        color: "#2196f3",
        icon: "",
        sortOrder:
          allStatuses.length > 0
            ? Math.max(...allStatuses.map((s) => s.sortOrder)) + 1
            : 1,
        isActive: true,
        canTransitionTo: [],
        requiresTrackingNumber: false,
        requiresNote: false,
      });
    }
    setErrors({});
  }, [mode, status, allStatuses, open]);

  const handleInputChange = (
    field: keyof CreateOrderStatusRequest,
    value: any,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof OrderStatusFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTransitionChange = (statusIds: number[]) => {
    handleInputChange("canTransitionTo", statusIds);
  };

  const validateForm = async (): Promise<boolean> => {
    const newErrors: OrderStatusFormErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = t("admin.orderStatusForm.nameRequired");
    } else if (!/^[A-Za-z0-9_-]+$/.test(formData.name)) {
      newErrors.name =
        t("admin.orderStatusForm.nameInvalidFormat");
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = t("admin.orderStatusForm.displayNameRequired");
    }

    if (!formData.color) {
      newErrors.color = t("admin.orderStatusForm.colorRequired");
    } else if (!/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = t("admin.orderStatusForm.colorInvalidFormat");
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = t("admin.orderStatusForm.sortOrderPositive");
    }

    // Check for duplicate names (excluding current status in edit mode)
    const existingStatus = allStatuses.find(
      (s) =>
        s.name === formData.name && (mode === "create" || s.id !== status?.id),
    );
    if (existingStatus) {
      newErrors.name = t("admin.orderStatusForm.nameDuplicate");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!(await validateForm())) return;

    try {
      setIsSubmitting(true);

      // Validate with backend
      let validation;
      if (mode === "create") {
        validation = await validateStatus.mutateAsync(formData);
      } else {
        validation = await validateStatusUpdate.mutateAsync({
          id: status!.id,
          request: formData,
        });
      }

      if (!validation.data?.isValid) {
        const validationErrors: OrderStatusFormErrors = {};
        validation.data?.errors?.forEach((error) => {
          // Map backend errors to form fields
          if (error.includes("name")) validationErrors.name = error;
          else if (error.includes("displayName"))
            validationErrors.displayName = error;
          else if (error.includes("color")) validationErrors.color = error;
          else if (error.includes("sortOrder"))
            validationErrors.sortOrder = error;
          else validationErrors.name = error; // Default to name field
        });
        setErrors(validationErrors);
        return;
      }

      // If validation passes, call onSuccess
      // The parent component will handle the actual API call
      onSuccess(formData as any);
    } catch (error) {
      console.error("Validation error:", error);
      setErrors({ name: t("admin.orderStatusForm.validationFailed") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      color: "#2196f3",
      icon: "",
      sortOrder: 0,
      isActive: true,
      canTransitionTo: [],
      requiresTrackingNumber: false,
      requiresNote: false,
    });
    setErrors({});
    onClose();
  };

  const availableTransitions = allStatuses.filter(
    (s) => mode === "create" || s.id !== status?.id,
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {mode === "create"
              ? t("admin.orderStatusForm.createOrderStatus")
              : mode === "edit"
                ? t("admin.orderStatusForm.editOrderStatus")
                : t("admin.orderStatusForm.viewOrderStatus")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t("admin.userForm.basicInformation")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t("admin.orderStatusForm.name")} *`}
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={
                errors.name ||
                t("admin.orderStatusForm.nameHelperText")
              }
              disabled={mode === "view" || isSubmitting}
              placeholder="e.g., pending, shipped, delivered"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t("admin.orderStatusForm.displayName")} *`}
              value={formData.displayName}
              onChange={(e) => handleInputChange("displayName", e.target.value)}
              error={!!errors.displayName}
              helperText={errors.displayName}
              disabled={mode === "view" || isSubmitting}
              placeholder="e.g., Pending, Shipped, Delivered"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("admin.orderStatusForm.description")}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              multiline
              rows={2}
              disabled={mode === "view" || isSubmitting}
              placeholder={t("admin.orderStatusForm.descriptionPlaceholder")}
            />
          </Grid>

          {/* Appearance */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t("admin.orderStatusForm.appearance")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t("admin.orderStatusForm.color")} *`}
              value={formData.color}
              onChange={(e) => handleInputChange("color", e.target.value)}
              error={!!errors.color}
              helperText={errors.color || t("admin.orderStatusForm.colorHelperText")}
              disabled={mode === "view" || isSubmitting}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: formData.color,
                      borderRadius: 1,
                      mr: 1,
                      border: "1px solid #ccc",
                    }}
                  />
                ),
              }}
            />
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {colorOptions.map((color) => (
                <Tooltip key={color} title={color}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      borderRadius: 1,
                      cursor: mode === "view" ? "default" : "pointer",
                      border:
                        formData.color === color
                          ? "2px solid #000"
                          : "1px solid #ccc",
                      opacity: mode === "view" ? 0.5 : 1,
                    }}
                    onClick={() =>
                      mode !== "view" && handleInputChange("color", color)
                    }
                  />
                </Tooltip>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orderStatusForm.icon")}</InputLabel>
              <Select
                value={formData.icon}
                onChange={(e) => handleInputChange("icon", e.target.value)}
                label={t("admin.orderStatusForm.icon")}
                disabled={mode === "view" || isSubmitting}
              >
                <MenuItem value="">{t("admin.orderStatusForm.noIcon")}</MenuItem>
                {iconOptions.map((icon) => (
                  <MenuItem key={icon} value={icon}>
                    {icon}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Configuration */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t("admin.orderStatusForm.configuration")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t("admin.orderStatusForm.sortOrder")} *`}
              type="number"
              value={formData.sortOrder}
              onChange={(e) =>
                handleInputChange("sortOrder", parseInt(e.target.value) || 0)
              }
              error={!!errors.sortOrder}
              helperText={errors.sortOrder || t("admin.orderStatusForm.sortOrderHelperText")}
              disabled={mode === "view" || isSubmitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box display="flex" flexDirection="column" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange("isActive", e.target.checked)
                    }
                    disabled={mode === "view" || isSubmitting}
                  />
                }
                label={t("common.active")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresTrackingNumber}
                    onChange={(e) =>
                      handleInputChange(
                        "requiresTrackingNumber",
                        e.target.checked,
                      )
                    }
                    disabled={mode === "view" || isSubmitting}
                  />
                }
                label={t("admin.orderStatusForm.requiresTrackingNumber")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresNote}
                    onChange={(e) =>
                      handleInputChange("requiresNote", e.target.checked)
                    }
                    disabled={mode === "view" || isSubmitting}
                  />
                }
                label={t("admin.orderStatusForm.requiresNote")}
              />
            </Box>
          </Grid>

          {/* Transitions */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {t("admin.orderStatusForm.allowedTransitions")}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t("admin.orderStatusForm.selectTransitionsDescription")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={availableTransitions}
              getOptionLabel={(option) =>
                `${option.displayName} (${option.name})`
              }
              value={availableTransitions.filter((s) =>
                formData.canTransitionTo.includes(s.id),
              )}
              onChange={(_, value) =>
                handleTransitionChange(value.map((s) => s.id))
              }
              disabled={mode === "view" || isSubmitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.orderStatusForm.canTransitionTo")}
                  placeholder={t("admin.orderStatusForm.selectStatusesPlaceholder")}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.displayName}
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
            />
          </Grid>

          {/* Validation Errors */}
          {Object.keys(errors).length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>
                  {t("admin.orderStatusForm.fixErrors")}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {Object.values(errors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {mode === "view" ? t("common.close") : t("common.cancel")}
        </Button>
        {mode !== "view" && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting
              ? t("admin.orderStatusForm.validating")
              : mode === "create"
                ? t("admin.orderStatusForm.create")
                : t("common.update")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderStatusFormModal;
